import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://crypto-expense-management-backend.onrender.com';

// Types
export interface WorkflowInstance {
  id: number;
  workflow_config_id: number;
  item_type: string;
  item_id: number;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'timeout' | 'failed';
  initiated_by: number;
  started_at: string;
  completed_at?: string;
  context_data?: any;
  created_at: string;
  updated_at: string;
}

export interface WorkflowStep {
  id: number;
  workflow_instance_id: number;
  blueprint_id: number;
  step_number: number;
  step_name: string;
  assigned_user_id?: number;
  assigned_group_id?: number;
  status: 'pending' | 'in_progress' | 'completed' | 'approved' | 'rejected' | 'escalated' | 'delegated' | 'skipped' | 'timeout';
  step_notes?: string;
  rejection_reason?: string;
  escalation_reason?: string;
  started_at?: string;
  completed_at?: string;
  timeout_at?: string;
  step_data?: any;
  created_at: string;
  updated_at: string;
}

export interface WorkflowStatus {
  workflow_instance: WorkflowInstance;
  steps: WorkflowStep[];
  current_step?: {
    step_number: number;
    step_name: string;
    status: string;
    assigned_to: string;
  };
  progress: {
    completed_steps: number;
    total_steps: number;
    percentage: number;
  };
  summary: {
    status: string;
    current_step_name?: string;
    next_action?: string;
    estimated_completion?: string;
  };
}

export interface TakeStepActionRequest {
  step_instance_id: number;
  action: 'approve' | 'reject' | 'escalate' | 'delegate';
  notes?: string;
  rejection_reason?: string;
  escalation_reason?: string;
  delegated_to_user_id?: number;
}

export interface PendingTask {
  step_instance: WorkflowStep;
  workflow_instance: WorkflowInstance;
  item_info?: {
    type: string;
    id: number;
    expense?: {
      id: number;
      title: string;
      amount: number;
      currency: string;
      vendor?: string;
      description?: string;
    };
  };
  due_date?: string;
}

// API Service
export class WorkflowService {
  static async getExpenseWorkflowStatus(expenseId: number): Promise<WorkflowStatus> {
    const response = await axios.get(`${API_BASE_URL}/api/v1/expense-workflows/expenses/${expenseId}/workflow-status`);
    return response.data.workflow_status;
  }

  static async executeWorkflowAction(expenseId: number, actionRequest: TakeStepActionRequest): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/api/v1/expense-workflows/expenses/${expenseId}/workflow-action`, actionRequest);
    return response.data;
  }

  static async cancelWorkflow(expenseId: number, reason: string): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/api/v1/expense-workflows/expenses/${expenseId}/cancel`, { reason });
    return response.data;
  }

  static async getMyPendingTasks(): Promise<PendingTask[]> {
    const response = await axios.get(`${API_BASE_URL}/api/v1/expense-workflows/my-pending-tasks`);
    return response.data.pending_tasks;
  }

  static async getMyWorkflows(): Promise<any[]> {
    const response = await axios.get(`${API_BASE_URL}/api/v1/expense-workflows/my-expense-workflows`);
    return response.data;
  }

  static async isUserApproverForExpense(expenseId: number): Promise<boolean> {
    try {
      // Check if user has pending tasks for this expense
      const pendingTasks = await this.getMyPendingTasks();
      return pendingTasks.some(task => 
        task.item_info?.type === 'expense' && task.item_info?.id === expenseId
      );
    } catch (error) {
      return false;
    }
  }

  static async canUserViewExpense(expenseId: number): Promise<boolean> {
    try {
      // Try to get the expense - if user has access, they can view it
      await axios.get(`${API_BASE_URL}/api/v1/expenses/${expenseId}`);
      return true;
    } catch (error) {
      return false;
    }
  }
}
